<template>
  <div class="property-page">
    <SiteNavigationBar></SiteNavigationBar>
    <Transition name="modal">
      <AppModal
        v-if="authModalPromise"
        class="modal-container fix-page"
        window-class="max-w-[calc(100vw-16px)] max-h-[calc(100svh-32px)] min-[412px]:max-w-[480px] w-full bg-white rounded-md md:rounded-lg shadow-lg py-2 md:p-4 overflow-y-auto"
        @click="authModalPromise?.resolve()"
      >
        <SiteAuth class="w-full" :title="$t('auth.loginToSavePropertyHint')" />
      </AppModal>
    </Transition>
    <Transition
      enter-active-class="transition duration-[0.21] ease-in-out"
      enter-from-class="transform translate-y-[200%]"
      enter-to-class="transform translate-y-0"
      leave-active-class="transition duration-[0.1] ease-in-out"
      leave-from-class="transform translate-y-0"
      leave-to-class="transform translate-y-[200%]"
    >
      <div
        v-if="
          result &&
          !buttonsAreVisible &&
          !contactFormIsVisible &&
          !footerIsVisible
        "
        class="flex items-center h-[48px] fixed bottom-[20px] sm:bottom-[32px] z-[58] bg-primary rounded absolute-center-x -translate-x-1/2 shadow"
      >
        <AppButton
          class="text-white variant-invisible uppercase h-full max-md:text-sm min-[390px]:whitespace-nowrap px-3 py-2 rounded"
          to="#contact-form"
        >
          {{ $t('property.contactAgent') }}
        </AppButton>
        <!-- <div class="h-[24px] w-[2px] bg-white -mx-px"></div>
        <AppButton
          class="text-white variant-invisible uppercase h-full max-md:text-sm min-[390px]:whitespace-nowrap px-3 py-2 rounded-r"
          to="tel:9545369851"
        >
          {{ $t('property.requestShowing') }}
        </AppButton> -->
      </div>
    </Transition>
    <Transition name="modal">
      <AppModal
        v-if="showCalculator"
        class="modal-container fix-page"
        window-class="relative max-w-[calc(100vw-16px)] max-h-[calc(100svh-32px)] min-[952px]:max-w-[1030px] w-full bg-white rounded-md md:rounded-lg shadow-lg md:!p-4 overflow-y-auto"
        @click="showCalculator = false"
      >
        <AppButton
          class="icon-button variant-invisible ml-auto h-8 w-8 !absolute right-2 top-2"
          :icon="mdiClose"
          :aria-label="$t('common.closePopup')"
          @click="showCalculator = false"
        />
        <h2
          class="uppercase text-lg md:text-xl mb-2 pl-4 max-md:pt-2 max-w-[calc(100%-40px)]"
        >
          {{ $t('calculator.title') }}
        </h2>
        <SitePropertyMortgageCalculator
          class="!mb-0"
          :list-price="result.listPrice"
          :property-tax="result.taxAnnualAmount || 0"
          :yearly-association-fee="result.fee || 0"
          :hide-insurance-field="
            ['condo', 'multiFamily', 'apartment'].includes(result.subType)
          "
        >
          <template #heading><span class="hidden"></span></template>
        </SitePropertyMortgageCalculator>
        <div class="text-sm text-neutral-light px-4 max-md:py-4 md:pt-4">
          {{ $t('calculator.disclaimer') }}
        </div>
      </AppModal>
    </Transition>
    <VueEasyLightbox
      :visible="visibleRef"
      :imgs="imgsRef"
      :index="indexRef"
      :scroll-disabled="false"
      rotate-disabled
      move-disabled
      @hide="onHide"
      @on-prev="updateIndex"
      @on-next="updateIndex"
    >
      <template #prev-btn="{ prev }">
        <button
          class="btn-prev absolute top-1/2 left-2 transform -translate-y-1/2 bg-white rounded-full p-2 z-50 opacity-60 focus:outline-none"
          :disabled="indexRef === 0"
          @click="prev"
        >
          <Icon class="text-neutral-light w-8 h-8" :path="mdiChevronLeft" />
        </button>
      </template>

      <template #next-btn="{ next }">
        <button
          class="btn-next absolute top-1/2 right-2 transform -translate-y-1/2 bg-white rounded-full p-2 z-50 opacity-60 focus:outline-none"
          :disabled="indexRef === imgsRef?.length - 1"
          @click="next"
        >
          <Icon class="text-neutral-light w-8 h-8" :path="mdiChevronRight" />
        </button>
      </template>
    </VueEasyLightbox>
    <main
      class="w-full !min-h-[100lvh] min-h-[100vh] px-4 pt-[calc(60px+1rem)] md:pt-[calc(88px+1rem)] pb-8"
    >
      <div
        id="search-bar-container"
        class="h-[64px] pt-2 flex justify-center w-[calc(100%-0.075rem)] -ml-4 -mt-4 bg-white fixed z-[51]"
      >
        <SiteSearchBar
          class="variant-outline-lg !w-[calc(100vw-32px)] max-w-[1280px] mx-auto h-[48px] fix-h"
          field-name="search-bar"
          :placeholder="$t('search.searchPlaceholder')"
          use-location-from-store
          @update:model-value="searchLocation = $event"
        >
          <template #button>
            <Icon
              class="text-neutral-light w-8 h-full"
              :path="mdiMapMarkerOutline"
            />
          </template>
        </SiteSearchBar>
      </div>
      <section
        id="property-info-section"
        class="max-w-[1280px] mx-auto pt-[44px] md:pt-[36px]"
      >
        <div
          class="flex gap-x-4 pb-1 md:pb-4 md:pt-4 items-center w-[calc(100%-1rem)] -ml-2 max-md:flex-wrap"
        >
          <AppButton
            v-if="projectLink"
            class="variant-invisible pl-1 pr-2 h-[40px] rounded"
            :to="projectLink"
          >
            <Icon :path="mdiArrowLeft" />
            {{ $t('property.backToProject') }}
          </AppButton>
          <AppButton
            v-else
            class="variant-invisible pl-1 pr-2 h-[40px] rounded"
            :to="searchLink"
            @click="backToSearchEvent"
          >
            <Icon :path="mdiArrowLeft" />
            {{ $t('property.backToSearch') }}
          </AppButton>
        </div>
        <div
          v-if="summary"
          id="property-summary"
          class="text-neutral-light text-sm mb-4"
        >
          {{ summary }}
        </div>
        <div
          v-if="result"
          class="flex flex-wrap justify-center items-stretch gap-6 max-md:max-w-[480px] max-md:mx-auto"
        >
          <div
            class="relative w-full max-w-[100vw] sm:min-w-[320px] lg:min-w-[440px] md:w-1/2 max-[470px]:mx-[-2rem] max-[470px]:w-[calc(100%+4rem)] min-[470px]:rounded-lg overflow-hidden"
          >
            <Splide
              v-if="images && images.length > 1 && !soldLabel"
              ref="splide"
              :options="{
                perPage: 1,
                perMove: 1,
                pagination: false,
                lazyLoad: 'nearby',
                loop: true,
                role: 'group'
              }"
              class="max-md:max-w-[480px] aspect-[4/3] overflow-hidden"
            >
              <SplideSlide
                v-for="({ image, imageSet, imageAlt }, index) in (
                  images || []
                ).filter(i => i.image)"
                :key="`${index}-slide`"
              >
                <img
                  :data-splide-lazy="image"
                  :data-splide-lazy-srcset="imageSet"
                  :alt="imageAlt"
                  class="w-full max-md:max-w-full aspect-[4/3] object-cover object-center cursor-pointer rounded-lg"
                  @click="() => show(index)"
                />
              </SplideSlide>
            </Splide>
            <template v-else>
              <img
                class="max-md:max-w-[480px] aspect-[4/3] h-full w-full object-cover object-center"
                :class="{
                  'bg-white !object-contain': images[0].placeholder,
                  'cursor-pointer': !images[0].placeholder,
                  'opacity-50': soldLabel
                }"
                :src="images[0].image"
                :srcset="images[0].imageSet"
                :alt="images[0].imageAlt"
                @click="() => (images[0].placeholder ? null : show(0))"
              />
              <div v-if="soldLabel" class="ribbon">
                <span>{{ soldLabel }}</span>
              </div>
            </template>
          </div>
          <div
            id="property-info"
            class="flex-1 max-lg:leading-7 leading-8 gap-1"
          >
            <div class="relative flex flex-col max-lg:max-w-[480px]">
              <div class="absolute -top-1 -right-1 flex gap-0.5">
                <AppButton
                  class="ml-auto variant-invisible icon-button h-[48px] w-[48px] min-w-[48px] rounded-full"
                  :icon="!favorites[result?.id] ? mdiHeartOutline : mdiHeart"
                  :disabled="!result?.id"
                  :title="$t('property.save')"
                  :aria-label="$t('property.save')"
                  @click="setPropertyAsFavorite(result, !favorites[result?.id])"
                />
                <AppButton
                  class="variant-invisible icon-button h-[48px] w-[48px] min-w-[48px] rounded-full"
                  :icon="mdiShareVariant"
                  :title="$t('property.share')"
                  :aria-label="$t('property.share')"
                  @click="shareDialogIsVisible = true"
                />
                <ShareDialog
                  :class="{
                    'opacity-0 pointer-events-none': !shareDialogIsVisible
                  }"
                  class="transition duration-300 ease-in-out"
                  :url="link"
                  :title="title"
                  @close="shareDialogIsVisible = false"
                />
              </div>

              <div class="flex items-center font-bold gap-2 mb-1">
                <span
                  class="h-3 w-3 rounded-full"
                  :class="[
                    ['Pending', 'Closed', 'Rented'].includes(result.status)
                      ? 'bg-neutral-lighter'
                      : 'bg-green'
                  ]"
                ></span>
                <span> {{ statusLabel }} </span>
              </div>
              <div class="font-bold text-2xl mb-3">
                {{ $n(+result.listPrice || 0, 'currency', language) }}
              </div>
              <div
                v-if="result?.listPrice && result?.type === 'RES'"
                class="px-4 py-1 flex flex-wrap items-center gap-x-2 bg-[#E0F2FF] rounded-sm mb-4"
              >
                <span class="whitespace-nowrap">
                  {{ $t('property.estimatedPayment') }}:
                </span>
                <span class="font-bold whitespace-nowrap">
                  {{
                    $n(estimatedPayment, 'currency', language) +
                    $t('common.perMonthNumber')
                  }}
                </span>
                <button
                  class="text-blue font-bold whitespace-nowrap"
                  type="button"
                  @click="showCalculator = true"
                >
                  {{ $t('property.getPrequalification') }}
                </button>
              </div>
              <div class="flex items-center gap-6 mb-2">
                <div v-if="result.bedrooms" class="">
                  <span class="font-bold">{{ result.bedrooms }}</span>
                  {{ $t('property.bedrooms').toLowerCase() }}
                </div>
                <div
                  v-if="result.bathsFull + (result.bathsHalf || 0) / 2"
                  class=""
                >
                  <span class="font-bold">
                    {{ result.bathsFull + (result.bathsHalf || 0) / 2 }}
                  </span>
                  {{ $t('property.bathrooms').toLowerCase() }}
                </div>
                <div v-if="result.propertyArea" class="">
                  <span class="font-bold">
                    {{ result.propertyArea }}
                  </span>
                  {{ $t('search.areaShort').toLowerCase() }}
                </div>
              </div>
              <h1 class="mb-2 leading-5">
                {{ title }}. {{ $t('property.mlsCode') }}{{ result.id }}
              </h1>
              <div
                class="grid grid-cols-2 gap-x-6 gap-y-3 max-lg:text-sm text-base mb-5"
              >
                <SiteDetailsProperty
                  v-if="subTypeText"
                  class="w-full min-w-[160px]"
                  :icon="mdiHomeVariant"
                  :value="subTypeText"
                  :label="$t('property.type')"
                />
                <SiteDetailsProperty
                  v-if="
                    result.propertyArea > 0 &&
                    !isNaN(result.listPrice / result.propertyArea)
                  "
                  class="w-full min-w-[160px]"
                  :icon="mdiCash"
                  :value="
                    $n(
                      +(result.listPrice / result.propertyArea).toFixed(2),
                      'currency',
                      language
                    )
                  "
                  :label="$t('project.priceSQFT')"
                />
                <SiteDetailsProperty
                  v-if="result.yearBuilt"
                  class="w-full min-w-[160px]"
                  :icon="mdiHammer"
                  :value="result.yearBuilt"
                  :label="$t('project.yearBuilt')"
                />
                <SiteDetailsProperty
                  v-if="result.fee"
                  class="w-full min-w-[160px]"
                  :icon="mdiCash100"
                  :value="$n(+result.fee.toFixed(2), 'currency', language)"
                  :label="$t('property.hoa')"
                />
                <SiteDetailsProperty
                  v-if="result.type !== 'RNT' && result.listPrice"
                  class="w-full min-w-[160px]"
                  :icon="mdiHomePercent"
                  :value="
                    $n(
                      +((result.listPrice * 0.02) / 12).toFixed(2),
                      'currency',
                      language
                    )
                  "
                  :label="$t('calculator.propertyTax')"
                />
              </div>
              <div
                id="contact-buttons"
                v-element-visibility="(v: boolean) => buttonsAreVisible = v"
                class="flex gap-4 text-base mb-4"
              >
                <AppButton
                  class="max-lg:text-sm px-4 max-lg:h-[40px] h-[48px] uppercase rounded shadow"
                  to="#contact-form"
                >
                  {{ $t('property.contactAgent') }}
                </AppButton>
                <!-- <AppButton
                  class="max-lg:text-sm px-4 max-lg:h-[40px] h-[48px] uppercase rounded shadow"
                  to="tel:9545369851"
                >
                  {{ $t('property.requestShowing') }}
                </AppButton> -->
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex flex-wrap justify-center items-stretch gap-6 max-md:max-w-[480px] max-md:mx-auto"
        >
          <div
            class="w-full max-w-[100vw] sm:min-w-[320px] lg:min-w-[440px] md:w-1/2 max-[470px]:mx-[-2rem] max-[470px]:w-[calc(100%+4rem)]"
          >
            <div
              class="max-md:max-w-[480px] aspect-[4/3] rounded-lg bg-gray-lighter animate-pulse"
            ></div>
          </div>
          <div class="max-lg:max-w-[480px] flex flex-col gap-4 pt-2">
            <div
              class="w-[160px] h-[28px] rounded bg-gray-lighter animate-pulse"
            ></div>
            <div
              class="w-[220px] h-[20px] rounded bg-gray-lighter animate-pulse"
            ></div>
            <div
              class="w-[240px] h-[20px] rounded bg-gray-lighter animate-pulse"
            ></div>
            <div
              class="w-[180px] h-[20px] rounded bg-gray-lighter animate-pulse"
            ></div>
            <div
              class="w-[200px] h-[20px] rounded bg-gray-lighter animate-pulse"
            ></div>
            <div class="flex gap-3 h-[88px] mt-5 mb-3">
              <div
                class="w-[78px] h-full rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-[78px] h-full rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-[92px] h-full rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-[140px] h-full rounded bg-gray-lighter animate-pulse"
              ></div>
            </div>
            <div
              class="w-[200px] h-[20px] rounded bg-gray-lighter animate-pulse"
            ></div>
            <div class="flex gap-3 h-[48px] mt-1 mb-4">
              <div
                class="w-[150px] h-full rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-[150px] h-full rounded bg-gray-lighter animate-pulse"
              ></div>
            </div>
          </div>
        </div>
      </section>
      <div
        class="flex flex-wrap justify-center items-start gap-12 max-md:max-w-[480px] max-w-[1280px] mx-auto md:mb-6"
      >
        <div class="max-w-[768px] min-w-[250px] mt-4 md:mt-10 mr-auto flex-1">
          <section id="more-details" class="mb-5">
            <div
              v-if="result?.remarks && result.remarks.length > 3"
              class="mb-6"
            >
              <h2 class="uppercase text-lg md:text-xl mb-4">
                {{ $t('project.propertyDetails') }}
              </h2>
              <p>{{ result.remarks }}</p>
            </div>
            <div v-else-if="!result" class="w-full flex flex-col gap-4 mb-6">
              <div
                class="w-[160px] h-[28px] rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-full h-[20px] rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-full h-[20px] rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-full h-[20px] rounded bg-gray-lighter animate-pulse"
              ></div>
              <div
                class="w-1/2 h-[20px] rounded bg-gray-lighter animate-pulse"
              ></div>
            </div>
            <ClientOnly>
              <div id="map-container" class="w-full !h-[250px] mb-10">
                <GMapMap
                  ref="map"
                  class="w-full h-full border border-line rounded-lg overflow-hidden"
                  :api-key="gMapsKey"
                  :center="
                    marker?.position || { lat: 25.761681, lng: -80.191788 }
                  "
                  :zoom="marker?.position ? DETAILED_ZOOM : GENERAL_ZOOM"
                  map-type-id="terrain"
                >
                  <GMapMarker v-if="marker" :options="marker" />
                </GMapMap>
              </div>
            </ClientOnly>
            <div v-if="result" class="mb-6 features-section">
              <h2 class="uppercase text-lg md:text-xl mb-4">
                {{ $t('property.factsAndFeatures') }}
              </h2>
              <details class="border-b border-line mb-6">
                <summary class="flex items-center gap-2 min-h-[40px]">
                  <Icon class="text-neutral w-6 h-6" :path="mdiFridge" />
                  <h3>
                    {{ $t('property.interior') }}
                  </h3>
                  <Icon
                    class="arrow text-neutral-light w-6 h-6 ml-auto"
                    :path="mdiChevronDown"
                  />
                </summary>
                <div class="content features-lists">
                  <div>
                    <h4>
                      {{ $t('property.bedroomsAndBathrooms') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.bedroomsLong') }}: {{ result.bedrooms }}
                      </li>
                      <li>
                        {{ $t('property.bathroomsLong') }}:
                        {{ result.bathsFull + (result.bathsHalf || 0) }}
                      </li>
                      <li>
                        {{ $t('property.fullBathrooms') }}:
                        {{ result.bathsFull }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="result.flooring?.length">
                    <h4>
                      {{ $t('property.flooring') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.flooring') }}:
                        {{ result.flooring?.join(', ') }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.heating') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.heatingFeatures') }}:
                        {{ result.heating?.join(', ') || 'N/A' }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.cooling') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.hasCooling') }}:
                        {{
                          result.cooling?.length
                            ? $t('common.yes')
                            : $t('common.no')
                        }}
                      </li>
                      <li v-if="result.cooling?.length">
                        {{ $t('property.coolingFeatures') }}:
                        {{ result.cooling.join(', ') }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.appliances') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.appliancesIncluded') }}:
                        {{ result.appliances?.join(', ') || 'N/A' }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="false">
                    <h4>
                      {{ $t('property.interiorFeatures') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.hasCooling') }}: {{ result.cooling }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="result.virtualTourUrl">
                    <h4>
                      {{ $t('property.otherInteriorFeatures') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.virtualTour') }}:
                        <a
                          class="link-primary"
                          :href="result.virtualTourUrl"
                          target="_blank"
                          >{{ $t('common.link') }}</a
                        >
                      </li>
                    </ul>
                  </div>
                  <div v-else>
                    <h4>
                      {{ $t('property.otherInteriorFeatures') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.virtualTour') }}: {{ $t('common.no') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </details>

              <details class="border-b border-line mb-6">
                <summary class="flex items-center gap-2 min-h-[40px]">
                  <Icon
                    class="text-neutral w-6 h-6"
                    :path="mdiOfficeBuilding"
                  />
                  <h3>
                    {{ $t('property.property') }}
                  </h3>
                  <Icon
                    class="arrow text-neutral-light w-6 h-6 ml-auto"
                    :path="mdiChevronDown"
                  />
                </summary>
                <div class="content features-lists">
                  <div v-if="false">
                    <h4>
                      {{ $t('property.parking') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.parkingFeatures') }}:
                        {{ result.parking }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.property') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.poolFeatures') }}:
                        {{ result.pool?.join(', ') || 'N/A' }}
                      </li>
                      <li
                        v-if="result.pool?.length && Array.isArray(result.pool)"
                      >
                        {{ $t('property.privatePool') }}:
                        {{
                          result.poolPrivate
                            ? $t('common.yes')
                            : $t('common.no')
                        }}
                      </li>
                      <li
                        v-if="
                          result.exteriorFeatures?.length &&
                          Array.isArray(result.exteriorFeatures)
                        "
                      >
                        {{ $t('property.exteriorFeatures') }}:
                        {{ result.exteriorFeatures.join(', ') }}
                      </li>
                      <li
                        v-if="
                          result.patioAndPorchFeatures?.length &&
                          Array.isArray(result.patioAndPorchFeatures)
                        "
                      >
                        {{ $t('property.patioAndPorchFeatures') }}:
                        {{ result.patioAndPorchFeatures.join(', ') }}
                      </li>
                      <li
                        v-if="result.view?.length && Array.isArray(result.view)"
                      >
                        {{ $t('property.viewDescription') }}:
                        {{ result.view.join(', ') }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.otherPropertyInformation') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.parcelNumber') }}:
                        {{ result.parcelNumber }}
                      </li>
                      <li>
                        {{ $t('property.attachedToAnotherStructure') }}:
                        {{
                          result.propertyAttached
                            ? $t('common.yes')
                            : $t('common.no')
                        }}
                      </li>
                      <li v-if="result.otherEquipment?.length">
                        {{ $t('property.otherEquipment') }}:
                        {{ result.otherEquipment.join(', ') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </details>
              <details class="border-b border-line mb-6">
                <summary class="flex items-center gap-2 min-h-[40px]">
                  <Icon
                    class="text-neutral w-6 h-6"
                    :path="mdiAccountHardHat"
                  />
                  <h3>
                    {{ $t('property.construction') }}
                  </h3>
                  <Icon
                    class="arrow text-neutral-light w-6 h-6 ml-auto"
                    :path="mdiChevronDown"
                  />
                </summary>
                <div class="content features-lists">
                  <div v-if="result.style?.length">
                    <h4>
                      {{ $t('property.typeAndStyle') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.homeType') }}:
                        {{ result.style.join(', ') }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="result.construction?.length">
                    <h4>
                      {{ $t('property.materialInformation') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.construction') }}:
                        {{ result.construction.join(', ') }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.condition') }}
                    </h4>
                    <ul>
                      <li v-if="result.propertyCondition">
                        {{ $t('property.propertyCondition') }}:
                        {{ result.propertyCondition }}
                      </li>
                      <li v-if="result.newConstruction">
                        {{ $t('property.newConstruction') }}:
                        {{ result.newConstruction }}
                      </li>
                      <li>
                        {{ $t('property.yearBuilt') }}: {{ result.yearBuilt }}
                      </li>
                    </ul>
                  </div>
                </div>
              </details>
              <details class="border-b border-line mb-6">
                <summary class="flex items-center gap-2 min-h-[40px]">
                  <Icon class="text-neutral w-6 h-6" :path="mdiHomeGroup" />
                  <h3>
                    {{ $t('property.communityAndNeighborhood') }}
                  </h3>
                  <Icon
                    class="arrow text-neutral-light w-6 h-6 ml-auto"
                    :path="mdiChevronDown"
                  />
                </summary>
                <div class="content features-lists">
                  <div v-if="result.homeType">
                    <h4>
                      {{ $t('property.security') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.homeType') }}: {{ result.homeType }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="result.city || result.neighborhood">
                    <h4>
                      {{ $t('property.location') }}
                    </h4>
                    <ul>
                      <li v-if="result.city">
                        {{ $t('property.region') }}: {{ result.city }}
                      </li>
                      <li v-if="result.neighborhood">
                        {{ $t('property.neighborhood') }}:
                        {{ result.neighborhood }}
                      </li>
                    </ul>
                  </div>
                </div>
              </details>
              <details class="border-b border-line mb-6">
                <summary class="flex items-center gap-2 min-h-[40px]">
                  <Icon class="text-neutral w-6 h-6" :path="mdiCurrencyUsd" />
                  <h3>
                    {{ $t('property.hoaAndFinancial') }}
                  </h3>
                  <Icon
                    class="arrow text-neutral-light w-6 h-6 ml-auto"
                    :path="mdiChevronDown"
                  />
                </summary>
                <div class="content features-lists">
                  <div>
                    <h4>
                      {{ $t('property.hoa') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.hasHoa') }}:
                        {{ result.hasHoa ? $t('common.yes') : $t('common.no') }}
                      </li>
                      <li>
                        {{ $t('property.hoaFee') }}:
                        {{ result.fee || 'N/A' }}
                      </li>
                      <li>
                        {{ $t('property.amenitiesIncluded') }}:
                        {{ result.amenities?.join(', ') || 'None' }}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4>
                      {{ $t('property.otherFinancialInformation') }}
                    </h4>
                    <ul>
                      <li>
                        {{ $t('property.buyerAgencyCompensation') }}:
                        {{ result.buyerAgencyCompensation || 'N/A' }}
                      </li>
                    </ul>
                  </div>
                </div>
              </details>
            </div>
            <div
              v-if="false"
              class="text-neutral-light text-base grid grid-cols-2"
            >
              <!-- HOA Fee/Quarterly -->
              <template v-if="result.fee">
                <div class="details-label">
                  {{ $t('project.HOAFee') }}
                </div>
                <div class="details-value">$ {{ result.fee }}</div>
              </template>

              <!-- HOPA -->
              <template v-if="result.HOPA !== undefined">
                <div class="details-label">{{ $t('project.HOPA') }}</div>
                <div v-if="result.HOPA" class="details-value">
                  {{ $t('project.hopaEligible') }}
                </div>
                <div v-else class="details-value">
                  {{ $t('project.noHopa') }}
                </div>
              </template>

              <!-- Amenities -->
              <template v-if="result.amenities">
                <div class="details-label">{{ $t('project.amenities') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.amenities) }}
                </div>
              </template>

              <!-- Appliances -->
              <template v-if="result.appliances">
                <div class="details-label">{{ $t('project.appliances') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.appliances) }}
                </div>
              </template>

              <!-- Restrictions -->
              <template v-if="result.restrictions">
                <div class="details-label">
                  {{ $t('project.restrictions') }}
                </div>
                <div class="details-value">
                  {{ formatInfo(result.restrictions) }}
                </div>
              </template>

              <!-- Furnished -->
              <template v-if="result.furnished">
                <div class="details-label">
                  {{ $t('project.furnished') }}
                </div>
                <div class="details-value">
                  {{ formatInfo(result.furnished) }}
                </div>
              </template>

              <!-- Flooring -->
              <template v-if="result.flooring">
                <div class="details-label">{{ $t('project.flooring') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.flooring) }}
                </div>
              </template>

              <!-- Terms Considered -->
              <template v-if="result.termsConsidered">
                <div class="details-label">
                  {{ $t('project.termsConsidered') }}
                </div>
                <div class="details-value">
                  {{ formatInfo(result.termsConsidered) }}
                </div>
              </template>

              <!-- Year Built -->
              <template v-if="result.yearBuilt">
                <div class="details-label">{{ $t('project.yearBuilt') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.yearBuilt) }}
                </div>
              </template>

              <!-- Cooling -->
              <template v-if="result.cooling">
                <div class="details-label">{{ $t('project.cooling') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.cooling) }}
                </div>
              </template>

              <!-- Pets Allowed -->
              <template v-if="result.petsAllowed">
                <div class="details-label">{{ $t('project.petsAllowed') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.petsAllowed) }}
                </div>
              </template>

              <!-- Interior -->
              <template v-if="result.interior">
                <div class="details-label">{{ $t('project.interior') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.interior) }}
                </div>
              </template>

              <!-- Maintenance Fee -->
              <template v-if="result.fee">
                <div class="details-label">
                  {{ $t('project.maintenanceFee') }}
                </div>
                <div class="details-value">
                  {{ result.fee }} {{ result.frequency || '' }}
                </div>
              </template>

              <!-- Construction -->
              <template v-if="result.construction">
                <div class="details-label">
                  {{ $t('project.construction') }}
                </div>
                <div class="details-value">
                  {{ formatInfo(result.construction) }}
                </div>
              </template>

              <!-- Heating -->
              <template v-if="result.heating">
                <div class="details-label">{{ $t('project.heating') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.heating) }}
                </div>
              </template>

              <!-- Garage/Carport -->
              <template v-if="result.garageCarport">
                <div class="details-label">{{ $t('project.garage') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.garageCarport) }}
                </div>
              </template>

              <!-- Exterior -->
              <template v-if="result.exterior">
                <div class="details-label">{{ $t('project.exterior') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.exterior) }}
                </div>
              </template>

              <!-- Parking -->
              <template v-if="result.parkingFeatures">
                <div class="details-label">{{ $t('project.parking') }}</div>
                <div class="details-value">
                  {{ formatInfo(result.parkingFeatures) }}
                </div>
              </template>
            </div>
          </section>
          <SiteBreadcrumbs
            class="flex-1 min-[432px]:min-w-[400px] max-w-[calc(100%-24px)] !p-0 mb-4"
            :items="breadcrumbs"
          />
          <section
            v-if="similarPropertiesCards.length || !similarPropertiesResponse"
            id="similar-properties"
            class="max-w-[1280px] max-md:max-w-[480px] mx-auto mb-8"
          >
            <h3 class="uppercase text-lg md:text-xl mb-4">
              {{ $t('property.similarProperties') }}
            </h3>
            <div
              class="max-sm:grid max-sm:grid-cols-1 max-sm:w-auto max-sm:mx-auto sm:flex flex-wrap gap-4 max-w-[928px]"
            >
              <template v-if="similarPropertiesCards.length">
                <SitePropertyCard
                  v-for="item in similarPropertiesCards"
                  :key="`${item.id}-card`"
                  :favorite="favorites[item.id]"
                  class="max-sm:w-full max-sm:aspect-auto"
                  v-bind="item"
                  :h="4"
                  show-favorite
                  @update:favorite="setPropertyAsFavorite(item, $event)"
                ></SitePropertyCard>
              </template>
              <template v-else>
                <SitePropertyCardPlaceholder
                  v-for="(item, index) in Array.from({
                    length: SIMILAR_PROPERTIES
                  })"
                  :key="`${index}-placeholder`"
                >
                </SitePropertyCardPlaceholder>
              </template>
            </div>
          </section>
          <section
            v-if="otherPropertiesCards.length"
            id="other-properties"
            class="max-w-[1280px] max-md:max-w-[480px] mx-auto mb-8"
          >
            <h3 class="uppercase text-lg md:text-xl mb-4">
              {{ $t('search.otherItems') }}
            </h3>
            <div
              class="max-sm:grid max-sm:grid-cols-1 max-sm:w-auto max-sm:mx-auto sm:flex flex-wrap gap-4 max-w-[928px]"
            >
              <SitePropertyCard
                v-for="item in otherPropertiesCards"
                :key="`${item?.id}-card`"
                :favorite="favorites[item?.id]"
                class="max-sm:w-full max-sm:aspect-auto"
                v-bind="item"
                :h="4"
                show-favorite
                @update:favorite="setPropertyAsFavorite(item, $event)"
              ></SitePropertyCard>
            </div>
          </section>
          <section id="disclaimer" class="">
            <NuxtImg
              :src="`${imageHost}/images/miami-realtors-logo-info.png`"
              loading="lazy"
              alt="Miami Realtors"
              class="object-contain object-center mb-4"
              provider="custom"
              width="116"
              height="32"
              format="webp"
              fit="scale-down"
              densities="x1 x2"
            />
            <p>
              {{ $t('common.infoDisclaimer') }}
            </p>
          </section>
        </div>
        <div class="side-section max-w-[405px] md:sticky md:top-[158px]">
          <div
            v-element-visibility="(v: boolean) => contactFormIsVisible = v"
            class="w-full self-start border border-line-light rounded-lg shadow-xl-right px-3 py-4 text-center"
          >
            <div id="contact-form" class="-mt-[116px] pt-[116px]"></div>
            <h5 class="text-lg font-bold mb-2">
              {{ $t('contactForm.title') }}
            </h5>
            <p class="text-[14px] mb-2 [text-wrap:balance]">
              {{ $t('contactForm.description') }}
            </p>
            <HubspotForm
              element-id="property-form"
              :form-id="{
                en: 'b39fa0b0-1c2a-4c2d-94e4-e352f3f0ef5e',
                es: '8f3d0898-565b-46fd-996e-f32d53676de0'
              }"
            />
          </div>
        </div>
      </div>
    </main>
    <SiteFooter
      v-once
      v-element-visibility="(v: boolean) => footerIsVisible = v"
      :fix-locale="locale"
    />
  </div>
</template>

<script setup lang="ts">
import {
  mdiAccountHardHat,
  mdiArrowLeft,
  mdiCash,
  mdiCash100,
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiClose,
  mdiCurrencyUsd,
  mdiFridge,
  mdiHammer,
  mdiHeart,
  mdiHeartOutline,
  mdiHomeGroup,
  mdiHomePercent,
  mdiHomeVariant,
  mdiMapMarkerOutline,
  mdiOfficeBuilding,
  mdiShareVariant
} from '@mdi/js';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css/core';
import VueEasyLightbox, { useEasyLightbox } from 'vue-easy-lightbox';
import { vElementVisibility } from '@vueuse/components';
import { useShare } from '@vueuse/core';
import { GoogleMap as GMapMap, Marker as GMapMarker } from 'vue3-google-map';

import { fixImageUrl } from '@/helpers/fixes';
import { propertySubTypeOptions, propertyTypeMap } from '@/helpers/search';
import { animateDetailsElements } from '@/helpers/utils';
import { useSearchStore } from '@/store/search';
import { ExternalPromise } from '@/types/common';
import type { QueryValue } from '@/types/common';
import type {
  PropertyCardProps,
  PropertyDocumentResponse,
  QueryResponse
} from '@/types/properties';

const AppModal = defineAsyncComponent(
  () => import('@/async-components/App/Modal.vue')
);
const HubspotForm = defineAsyncComponent(
  () => import('@/async-components/HubspotForm.vue')
);
const SiteAuth = defineAsyncComponent(
  () => import('@/async-components/Site/Auth.vue')
);
const SiteBreadcrumbs = defineAsyncComponent(
  () => import('@/async-components/Site/Breadcrumbs.vue')
);
const SiteNavigationBar = defineAsyncComponent(
  () => import('@/async-components/Site/NavigationBar.vue')
);
const SiteDetailsProperty = defineAsyncComponent(
  () => import('@/async-components/Site/Details/Property.vue')
);
const SitePropertyCardPlaceholder = defineAsyncComponent(
  () => import('@/async-components/Site/PropertyCardPlaceholder.vue')
);
const SitePropertyCard = defineAsyncComponent(
  () => import('@/async-components/Site/PropertyCard.vue')
);
const SitePropertyMortgageCalculator = defineAsyncComponent(
  () => import('@/async-components/Site/Property/MortgageCalculator.vue')
);
const SiteSearchBar = defineAsyncComponent(
  () => import('@/async-components/Site/Search/Bar.vue')
);
const ShareDialog = defineAsyncComponent(
  () => import('@/async-components/App/ShareDialog.vue')
);

const props = defineProps({
  preloadedResponse: {
    type: Object as PropType<QueryResponse<PropertyDocumentResponse>>,
    required: true
  }
});

const {
  public: { apiUrl, gMapsKey }
} = useRuntimeConfig();

const { get } = useHttpMethods();

const user = inject('user') as Ref<{ email?: unknown } | null>;
const isAuthenticated = computed(() => Boolean(user?.value?.email));

const { language } = useLanguage();

const localePath = useLocalePath();

const {
  rentPrices,
  buyPrices,
  areaOptions,
  possibleParamsKeys,
  createSearchParams,
  getSearchParameters,
  getBreadcrumbs
} = useSearch();

const { t, locale } = useI18n();

function tl(key: string, locale: string = 'en') {
  return t(key, null, { locale });
}

const { triggerEvent } = useHubspot();

const {
  // methods
  show,
  onHide,
  // refs
  visibleRef,
  indexRef,
  imgsRef
} = useEasyLightbox();

const { share } = useShare();

const DETAILED_ZOOM = 12;

const GENERAL_ZOOM = 7;

const contactButtons = ref(null);

const buttonsAreVisible = ref(true);

const footerIsVisible = ref(false);

const contactFormIsVisible = ref(false);

const SIMILAR_PROPERTIES = 6;

const result = ref<PropertyDocumentResponse | null>();

const map = ref(null);

const marker = computed(() => {
  if (!result.value?.geopoint) return null;

  return {
    position: {
      lat: result.value.geopoint[0],
      lng: result.value.geopoint[1]
    }
  };
});

const searchStore = useSearchStore();

const { selectedLocation: searchLocation } = useSelectableLocation(() => ({
  query: 'properties',
  navigateToLocations: true,
  buyType: result.value?.type,
  beforeNavigate: searchStore.saveQueryToStorage
}));

const summary = ref('');

const splide = ref<{ go: (index: number) => void } | null>(null);

const shareDialogIsVisible = ref(false);

const {
  public: { cloudflareImageProcessing: cfUrl }
} = useRuntimeConfig();

const images = computed<
  {
    image: string;
    imageSet?: string;
    imageAlt: string;
    placeholder?: boolean;
  }[]
>(() => {
  const images =
    (result.value?.photos || []).map((imageUrl: string) => {
      const fixedImage = fixImageUrl(imageUrl);
      const image = `${cfUrl}/image?w=640&h=480&f=webp&fit=crop&image=${fixedImage}`;
      return {
        image,
        imageSet: `${image} 1x, ${cfUrl}/image?w=1280&h=960&f=webp&fit=crop&image=${fixedImage} 2x`,
        imageAlt: result.value.full
      };
    }) || [];

  return images?.length
    ? images
    : [
        {
          image: '/images/pfs-placeholder.svg',
          imageAlt: 'PFS Realty Group',
          placeholder: true
        }
      ];
});

const soldLabel = computed(() => {
  const isFemale = propertyTypeMap[result.value.subType] === 'house';

  if (['Pending', 'Closed', 'Rented'].includes(result.value.status)) {
    if (result.value.type === 'RNT') {
      return isFemale ? t('property.rented_female') : t('property.rented');
    } else {
      return isFemale ? t('property.sold_female') : t('property.sold');
    }
  }

  return '';
});

const statusLabel = computed(() => {
  const isFemale = propertyTypeMap[result.value.subType] === 'house';

  return (
    soldLabel.value ||
    (isFemale ? t('property.active_female') : t('property.active'))
  );
});

const urlParams = useUrlParams();

const projectLink = computed(() => {
  return searchStore.projectLink && localePath(searchStore.projectLink);
});

const buildingFilters = computed(() => {
  return searchStore.projectLink ? searchStore.buildingFilters : null;
});

const searchLink = computed(() => {
  const searchParams: string[] = searchStore.propertiesSearchParams || [];

  if (!searchParams.length) {
    searchParams.push(...getSearchParameters({ buyType: 'RES' }, t));
  }
  return localePath(`/${t('routes.properties')}/${searchParams.join('/')}`);
});

function backToSearchEvent() {
  if (typeof window !== 'undefined') {
    window.goingBack = true;
  }
}

const resultIndex = ref<number | null>(null);

const previousId = ref<string | null>(null);
const nextId = ref<string | null>(null);

const previousLink = computed(() => {
  return null;

  // TODO: use localStorage

  // if (!previousId.value) return null;

  // const searchParams = [...urlParams.value];

  // const resultIndexParamIndex = searchParams.findIndex((param: string) =>
  //   param.includes('-result')
  // );

  // if (resultIndexParamIndex > -1) {
  //   searchParams[resultIndexParamIndex] = `${resultIndex.value - 1}-result`;
  // }

  // searchParams[searchParams.length - 1] = previousId.value;

  // return localePath(`/${t('routes.properties')}/` + searchParams.join('/'));
});

const nextLink = computed(() => {
  return null;

  // TODO: use localStorage

  // if (!nextId.value) return null;

  // const searchParams = [...urlParams.value];

  // const resultIndexParamIndex = searchParams.findIndex((param: string) =>
  //   param.includes('-result')
  // );

  // if (resultIndexParamIndex > -1) {
  //   searchParams[resultIndexParamIndex] = `${resultIndex.value + 1}-result`;
  // }

  // searchParams[searchParams.length - 1] = nextId.value;

  // return localePath(`/${t('routes.properties')}/` + searchParams.join('/'));
});

const similarPropertiesResponse =
  ref<QueryResponse<PropertyDocumentResponse> | null>(null);

const similarProperties = ref<PropertyDocumentResponse[]>([]);

const otherPropertiesResponse =
  ref<QueryResponse<PropertyDocumentResponse> | null>(null);

const otherProperties = ref<PropertyDocumentResponse[]>([]);

const {
  getPropertyLink,
  formatProperty,
  setFavorite,
  updateFavorites,
  favorites
} = useProperties();

const authModalPromise = ref<ExternalPromise<void> | null>(null);

const showCalculator = ref(false);

const { addToast } = useToasts();

async function setPropertyAsFavorite(
  property: PropertyDocumentResponse | PropertyCardProps,
  value: boolean
) {
  let check = false;
  if (!isAuthenticated.value) {
    check = true;
    authModalPromise.value = new ExternalPromise<void>();
    await authModalPromise.value;
    authModalPromise.value = null;
    if (!isAuthenticated.value) {
      return;
    }
  }

  const link =
    'to' in property
      ? property.to
      : localePath(
          `/${t('routes.properties')}/${getPropertyLink(
            property as PropertyDocumentResponse,
            t
          )}`
        );

  await setFavorite(property.id, value, check);
  triggerEvent('pe23281212_property_save', {
    saved: link
  });

  if (value) {
    addToast({
      title: t('property.propertySaved'),
      type: 'success'
    });
  } else {
    addToast({
      title: t('property.propertyRemoved'),
      type: 'info'
    });
  }
}

watch(isAuthenticated, () => {
  if (isAuthenticated.value && authModalPromise.value) {
    authModalPromise.value.resolve();
    authModalPromise.value = null;
  }
});

const similarPropertiesCards = computed<PropertyCardProps[]>(() => {
  return similarProperties.value.filter(Boolean).map(formatProperty);
});

const otherPropertiesCards = computed<PropertyCardProps[]>(() => {
  return otherProperties.value.filter(Boolean).map(formatProperty);
});

watch(
  () => [
    isAuthenticated.value,
    similarPropertiesCards.value,
    otherPropertiesCards.value
  ],
  async () => {
    if (!isAuthenticated.value) return;

    await updateFavorites(
      [
        result.value?.id,
        ...similarPropertiesCards.value.map(value => value.id),
        ...otherPropertiesCards.value.map(value => value.id)
      ].filter(Boolean)
    );
  }
);

watch(
  () => result.value,
  () => {
    imgsRef.value = result.value?.photos || [];
  }
);

function updateIndex(_oldIndex: number, index: number) {
  indexRef.value = index;
  if (splide.value) {
    splide.value.go(index);
  }
}

async function getSimilarProperties() {
  if (!result.value) return;

  // TODO: improve search to always return values

  similarPropertiesResponse.value = null;
  similarProperties.value = [];

  let queryParams = {
    perPage: SIMILAR_PROPERTIES.toString()
  } as Record<string, string | string[]>;

  const filterBy: string[] = [];

  if (result.value.bedrooms) {
    filterBy.push(`bedrooms:>=${result.value.bedrooms}`);
    filterBy.push(`bedrooms:<=${result.value.bedrooms + 3}`);
  }

  if (result.value.bathsFull) {
    filterBy.push(`bathsFull:>=${result.value.bathsFull}`);
    filterBy.push(`bathsFull:<=${result.value.bathsFull + 4}`);
  }

  if (result.value.yearBuilt) {
    filterBy.push(`yearBuilt:>=${result.value.yearBuilt - 7}`);
    filterBy.push(`yearBuilt:<=${result.value.yearBuilt + 7}`);
  }

  if (result.value.propertyArea) {
    filterBy.push(`propertyArea:>=${result.value.propertyArea - 250}`);
    filterBy.push(`propertyArea:<=${result.value.propertyArea + 250}`);
  }

  if (result.value.listPrice) {
    if (result.value.type === 'RNT') {
      filterBy.push(`listPrice:>=${result.value.listPrice - 1000}`);
      filterBy.push(`listPrice:<=${result.value.listPrice + 1000}`);
    } else {
      filterBy.push(`listPrice:>=${result.value.listPrice - 50000}`);
      filterBy.push(`listPrice:<=${result.value.listPrice + 50000}`);
    }
  }

  if (result.value.city) {
    filterBy.push(`city:=${result.value.city}`);
  }

  if (result.value.type) {
    filterBy.push(`type:=${result.value.type}`);
  }

  if (result.value.mlsId) {
    // TODO: Check if using `mlsId` instead of `id` is ok
    filterBy.push(`mlsId:!=${result.value.mlsId}`);
  }

  filterBy.push(`status:!=Rented`);
  filterBy.push(`status:!=Closed`);
  filterBy.push(`status:!=Pending`);

  if (filterBy.length > 0) {
    queryParams.filterBy = filterBy;
  }

  queryParams.sortBy = 'bedrooms:asc,listPrice:asc,bathsFull:asc';

  queryParams.includeFields =
    'bathsFull,bathsHalf,bedrooms,city,county,full,geopoint,id,listPrice,mlsId,photos,postalCode,propertyArea,state,streetName,subType,type';

  if (buildingFilters.value) {
    queryParams = {
      ...queryParams,
      ...buildingFilters.value
    };
  }

  const url =
    apiUrl +
    '/search/?' +
    Object.entries(createSearchParams(queryParams))
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');
  // new URLSearchParams(queryParams as Record<string, string>);

  similarPropertiesResponse.value = (
    await get<{ data: QueryResponse<PropertyDocumentResponse> }>(url)
  ).data;

  similarProperties.value =
    similarPropertiesResponse.value?.hits.map(hit => hit.document) || [];
}

async function getOtherProperties() {
  if (!result.value) return;

  // TODO: improve search to always return values

  otherPropertiesResponse.value = null;
  otherProperties.value = [];

  const queryParams = {
    perPage: SIMILAR_PROPERTIES.toString()
  } as Record<string, string | string[]>;

  const filterBy: string[] = [];

  if (result.value.bedrooms) {
    filterBy.push(`bedrooms:=${result.value.bedrooms}`);
  }

  if (result.value.bathsFull) {
    filterBy.push(`bathsFull:>=${result.value.bathsFull}`);
    filterBy.push(`bathsFull:<=${result.value.bathsFull + 1}`);
  }

  if (result.value.type) {
    filterBy.push(`type:=${result.value.type}`);
  }

  if (result.value.mlsId) {
    // TODO: Check if using `mlsId` instead of `id` is ok
    filterBy.push(`mlsId:!=${result.value.mlsId}`);
  }

  filterBy.push(`status:!=Rented`);
  filterBy.push(`status:!=Closed`);
  filterBy.push(`status:!=Pending`);

  if (result.value.geopoint && result.value.geopoint.length > 0) {
    queryParams.sortBy = `geopoint(${result.value.geopoint.join(',')}):asc`;
  }

  if (filterBy.length > 0) {
    queryParams.filterBy = filterBy;
  }

  queryParams.includeFields =
    'bathsFull,bathsHalf,bedrooms,city,county,full,geopoint,id,listPrice,mlsId,photos,postalCode,propertyArea,state,streetName,subType,type';

  const url =
    apiUrl +
    '/search/?' +
    Object.entries(createSearchParams(queryParams))
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');
  // new URLSearchParams(queryParams as Record<string, string>);

  otherPropertiesResponse.value = (
    await get<{ data: QueryResponse<PropertyDocumentResponse> }>(url)
  ).data;

  otherProperties.value =
    otherPropertiesResponse.value?.hits.map(hit => hit.document) || [];
}

function formatInfo(value: unknown) {
  if (!value) return '';

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'string' && value.startsWith('[')) {
    return value.replace("['", '').replace("']", '').replace("', '", ', ');
  }

  return value;
}

function fixPropertyResponse<T extends { subType: string }>(value: T): T {
  const newValue = { ...value };

  if (newValue.subType === 'multifamily') {
    newValue.subType = 'multiFamily';
  }

  return newValue;
}

async function loadProperty() {
  const response = props.preloadedResponse;

  if (!response.hits[0]) {
    console.error('[PROPERTY] Response without hits');
    return;
  }

  result.value = fixPropertyResponse(response.hits[0].document);

  const paramsObject = Object.fromEntries(
    urlParams.value.map(v => {
      const key = possibleParamsKeys.find(k => v.endsWith(`-${k}`));
      const underscoreKey = possibleParamsKeys.find(k => v.endsWith(`_${k}`));

      if (key) {
        return [key, v.replace(`-${key}`, '')];
      } else if (underscoreKey) {
        return [underscoreKey, v.replace(`-${underscoreKey}`, '')];
      } else {
        return v.includes('_')
          ? [v.split('_').slice(-1)[0], v.split('_').slice(0, -1).join('_')]
          : [v.split('-').slice(-1)[0], v.split('-').slice(0, -1).join('-')];
      }
    })
  );

  resultIndex.value = +paramsObject.result;

  if (resultIndex.value) {
    const hasPrevious = resultIndex.value > 1;

    // -2 since offset starts at 0 and indices start at 1
    const offset = Math.max(resultIndex.value - 2, 0);

    const limit = !hasPrevious ? 1 : 2;

    const queryParams = createSearchParams({
      ...paramsObject,
      offset,
      limit,
      omitPriceFilter: true,
      // TODO: Check if using `mlsId` instead of `id` is ok
      filterBy: ['mlsId:!=' + result.value.mlsId]
    });

    delete queryParams.page;

    const previousAndNextResponse = (
      await get<{ data: QueryResponse<PropertyDocumentResponse<'id'>> }>(
        apiUrl +
          '/search/?' +
          Object.entries({
            ...queryParams,
            include_fields: 'id,mlsId'
          })
            .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
            .join('&')
      )
    ).data;

    if (previousAndNextResponse.hits.length === 1) {
      if (hasPrevious) {
        previousId.value = previousAndNextResponse.hits[0].document.id;
      } else {
        nextId.value = previousAndNextResponse.hits[0].document.id;
      }
    } else if (previousAndNextResponse.hits.length === 2) {
      previousId.value = previousAndNextResponse.hits[0].document.id;
      nextId.value = previousAndNextResponse.hits[1].document.id;
    }
  }

  if (isAuthenticated.value) {
    updateFavorites([result.value.id], true);
  }

  await Promise.all([getSimilarProperties(), getOtherProperties()]);
}

let pathFixed = false;

function fixPath() {
  if (!result.value || pathFixed) {
    return;
  }

  const newPath = localePath(
    `/${t('routes.properties')}/${getPropertyLink(result.value, t)}`
  );

  if (newPath !== window.location.pathname) {
    navigateTo(newPath, { replace: true });
  }
  pathFixed = true;
}

function shareProperty() {
  share({
    title: t('property.shareTitle'),
    text: t('property.shareText'),
    url: window.location.href
  });
}

const copyShareLink = () => {
  navigator.clipboard.writeText(
    `${t('property.shareText')} ${window.location.href}`
  );
  addToast({
    title: t('property.shareLinkCopied'),
    type: 'success'
  });
};

const { data, error } = await useAsyncData(
  'property-component-data',
  async () => {
    if (process.client) {
      return {};
    }
    await loadProperty();
    return {
      result: result.value,
      resultIndex: resultIndex.value,
      previousId: previousId.value,
      nextId: nextId.value,
      similarPropertiesResponse: similarPropertiesResponse.value,
      similarPropertiesRaw: similarProperties.value,
      otherPropertiesResponse: otherPropertiesResponse.value,
      otherPropertiesRaw: otherProperties.value
    };
  }
);

let link: string = '';

if (error.value || (!data.value?.result && !process.client)) {
  console.error(error.value || '[PROPERTY] Property not loaded');
} else if (data.value?.result) {
  result.value = data.value.result;
  resultIndex.value = data.value.resultIndex;
  previousId.value = data.value.previousId;
  nextId.value = data.value.nextId;
  similarPropertiesResponse.value = data.value.similarPropertiesResponse;
  similarProperties.value = data.value.similarPropertiesRaw;
  otherPropertiesResponse.value = data.value.otherPropertiesResponse;
  otherProperties.value = data.value.otherPropertiesRaw;

  link = useCanonicalLink(
    t =>
      `/${t('routes.properties')}/` +
      (result.value ? getPropertyLink(result.value, t) : '')
  ).link;
}

const title = computed(() => {
  if (!result.value) return '';

  // propertyType: , TODO: types
  const propertyTypesMap = Object.fromEntries(
    propertySubTypeOptions.map(option => [option.value, t(option.typeText)])
  );

  let property =
    propertyTypesMap[result.value.subType as keyof typeof propertyTypesMap] ||
    t('seo.defaultSingle');

  if (result.value.type === 'RNT') {
    property = t('seo.singleRentAlt', [property]);
  } else if (result.value.type !== undefined) {
    property = t('seo.singleSaleAlt', [property]);
  }

  const address = `${result.value.full}`;

  if (address) {
    return t('connectors.in', [property, address]);
  } else {
    return property;
  }
});

const subTypeText = computed(() => {
  if (!result.value) return '';

  const subType = t(
    propertySubTypeOptions.find(option => option.value === result.value.subType)
      ?.text || ''
  );

  return subType;
});

const estimatedPayment = computed(() => {
  if (!result.value) {
    return 0;
  }
  const homePrice = result.value.listPrice;
  const yearlyPropertyTax = +(homePrice * 0.02); // 2%
  const monthlyAssociationFee = +(+result.value.fee.toFixed(4));
  const downPayment = homePrice * 0.4 > homePrice ? homePrice : homePrice * 0.4; // Ensure downPayment does not exceed homePrice
  const interestRate = 7.5; // 7.5%
  const loanType = 30;
  const yearlyInsuranceFeeDefault = Math.round(+homePrice * 0.007); // 0.7%
  const monthlyInsuranceFeeDefault = Math.round(yearlyInsuranceFeeDefault / 12);
  const monthlyInsuranceFee = ['condo', 'multiFamily', 'apartment'].includes(
    result.value.subType
  )
    ? 0
    : monthlyInsuranceFeeDefault;

  const principal = homePrice - downPayment;
  const monthlyInterestRate = interestRate / 100 / 12;
  const numberOfPayments = loanType * 12; // Loan type represents the number of years

  // Monthly mortgage payment
  const mortgagePayment =
    principal *
    (monthlyInterestRate /
      (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments)));

  const monthlyPropertyTax = yearlyPropertyTax / 12;

  return (
    mortgagePayment +
    monthlyPropertyTax +
    monthlyAssociationFee +
    monthlyInsuranceFee
  );
});

const breadcrumbs = computed(() => {
  if (!result.value) return [];

  const prices = result.value.type === 'RNT' ? rentPrices : buyPrices;

  const priceFrom = prices
    .filter(price => price.value < result.value.listPrice)
    .slice(-1)[0]?.value;

  const priceTo = prices.filter(
    price => price.value > result.value.listPrice
  )[0]?.value;

  const areaFrom = areaOptions
    .filter(area => area.value < result.value.propertyArea)
    .slice(-1)[0]?.value;

  const areaTo = areaOptions.filter(
    area => area.value > result.value.propertyArea
  )[0]?.value;

  const fixedPostalCode =
    (typeof result.value.postalCode === 'string'
      ? result.value.postalCode.split('.')?.[0]
      : null) || result.value.postalCode;

  const query: Record<string, QueryValue> = {
    ...result.value,
    areaFrom,
    areaTo,
    buyType: result.value.type,
    propertyType: [propertyTypeMap[result.value.subType]],
    propertySubType: [result.value.subType],
    property: result.value.id,
    bedrooms: result.value.bedrooms,
    bathrooms: result.value.bathsFull,
    priceFrom,
    priceTo,
    yearFrom: result.value.yearBuilt,
    county: result.value.county,
    city: result.value.city,
    zipcode: fixedPostalCode,
    neighborhood: result.value.neighborhood
  };

  const breadcrumbs = getBreadcrumbs(query, tl('routes.properties'));

  if (!breadcrumbs[breadcrumbs.length - 1].item) {
    breadcrumbs[breadcrumbs.length - 1] = {
      ...breadcrumbs[breadcrumbs.length - 1],
      item: localePath(
        `/${t('routes.properties')}/` +
          (result.value ? getPropertyLink(result.value, t) : '')
      ),
      interactuable: false
    };
  }

  return breadcrumbs;
});

const host = useHost();

const imageHost = useHost(true);

useSchemaOrg(
  [
    ...useCommonSchemaOrg(link),
    result.value
      ? definePlace({
          '@id': link + '#single-family-residence',
          '@type': 'SingleFamilyResidence',
          name: `${result.value.streetNumber} ${result.value.streetName}`,
          url:
            host +
            localePath(
              `/${t('routes.properties')}/` + getPropertyLink(result.value, t)
            ),
          description: result.value.remarks,
          numberOfRooms: result.value.bedrooms,
          numberOfBedrooms: result.value.bedrooms,
          image: images.value[0]?.image,
          numberOfFullBathrooms: result.value.bathsFull,
          numberOfPartialBathrooms: result.value.bathsHalf,
          numberOfBathroomsTotal:
            result.value.bathsFull + result.value.bathsHalf,
          yearBuilt: result.value.yearBuilt,
          ...(result.value.propertyArea
            ? {
                floorSize: {
                  '@type': 'QuantitativeValue',
                  value: result.value.propertyArea,
                  unitCode: 'FTK'
                }
              }
            : {}),
          address: {
            '@type': 'PostalAddress',
            streetAddress: `${result.value.streetNumber} ${result.value.streetName}`,
            addressLocality: result.value.city,
            addressRegion: result.value.state,
            postalCode: result.value.postalCode,
            addressCountry: 'US'
          }
        })
      : null,
    defineBreadcrumb({
      '@id': link + '#breadcrumb',
      itemListElement: breadcrumbs.value.map(({ name, item }) => ({
        name,
        item
      }))
    })
  ].filter(Boolean)
);

const { getPropertySeo } = useGetSeo();

useSeoExtended({
  title: () => (result.value ? getPropertySeo(result.value, 'title') : ''),
  description: () =>
    result.value ? getPropertySeo(result.value, 'description') : '',
  image: () => images.value[0]?.image || '/images/pfs-placeholder.svg'
});

useHead({
  meta: [
    {
      hid: 'og:locale',
      property: 'og:locale',
      content: t('seo.locale')
    },
    {
      hid: 'og:locale:alternate',
      property: 'og:locale:alternate',
      content: t('seo.alternateLocale')
    },
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image'
    }
  ]
});

onMounted(() => {
  fixPath();
  if (Object.keys(data.value).length === 0) {
    loadProperty().then(() => {
      useCanonicalLink(
        t =>
          `/${t('routes.properties')}/` +
          (result.value ? getPropertyLink(result.value, t) : '')
      );
    });
  }
  animateDetailsElements();
});
</script>

<style lang="scss">
.property-page .hubspotForm {
  iframe {
    min-height: 720px !important;
  }
}

body:has(.vel-modal) {
  overflow-y: scroll;
  position: fixed;
  width: 100%;
}

.vel-modal {
  @apply bg-black/90 #{!important};
}

.vel-btns-wrapper {
  .btn-next,
  .btn-prev {
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
}
</style>

<style lang="scss">
@import '~/assets/css/splide-theme.scss';
</style>

<style scoped lang="scss">
.ribbon {
  @apply absolute overflow-hidden;
  @apply top-0 left-0;
  @apply w-[300px] h-[300px] md:w-[500px] md:h-[500px];
}
.ribbon span {
  @apply absolute block py-2 -rotate-45;
  @apply bg-primary text-white text-center;
  @apply text-xl md:text-2xl;
  @apply w-[300px] origin-[150px_150px] md:w-[500px] md:origin-[250px_250px];
}

.features-section {
  .features-lists {
    @apply md:columns-2 pt-2 pb-4;
    & > div {
      @apply break-inside-avoid	w-full;
    }
  }
  h2 {
    @apply uppercase text-lg md:text-xl mb-4;
  }
  h3 {
    @apply uppercase text-lg;
  }
  h4 {
    @apply font-bold mb-2;
  }
  ul {
    @apply list-inside mb-4;
    list-style-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='-5 -5 10 10'><circle r='1' /></svg>");
  }
  li {
    @apply mb-1;
  }
}

details {
  summary {
    list-style: none;
    cursor: pointer;
    user-select: none;
    position: relative;
  }

  .arrow {
    @apply transition-transform duration-200;
  }

  &[open] {
    .arrow {
      transform: rotate(-180deg);
    }
  }
}
</style>
